import * as Result from "@gordonb/result/result";
import {
  type LibraryAttachmentWithUrl,
  type LibraryAttachmentWithUrlResult,
  LibraryAttachmentWithUrlResultSchema,
} from "../../models/library-attachment.ts";
import { toBearerToken } from "../../util/bearer-token.ts";
import { triggerDownload } from "../util/download.ts";
import { LIBRARY_ATTACHMENTS_API_URL } from "../client-config.ts";

/**
 * Fetch library attachment metadata with signed download URL.
 * @param token - Auth token
 * @param sha256 - Content hash identifying the attachment
 */
export const getLibraryAttachment = async (
  token: string,
  sha256: string,
): Promise<LibraryAttachmentWithUrlResult> => {
  try {
    const response = await fetch(`${LIBRARY_ATTACHMENTS_API_URL}/${sha256}`, {
      method: "GET",
      headers: {
        authorization: toBearerToken(token),
      },
    });
    const json = await response.json();
    return LibraryAttachmentWithUrlResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

/**
 * Download a library attachment and trigger browser download.
 * @param token - Auth token
 * @param sha256 - Content hash identifying the attachment
 * @param filename - Filename to use for the downloaded file
 */
export const downloadLibraryAttachment = async (
  token: string,
  sha256: string,
  filename: string,
): Promise<Result.Result<void, string>> => {
  // First get the signed URL using the content hash
  const attachmentResult = await getLibraryAttachment(token, sha256);

  if (!attachmentResult.ok) {
    return Result.err(attachmentResult.error);
  }

  const attachment: LibraryAttachmentWithUrl = attachmentResult.value;

  try {
    // Fetch the file as a blob
    const response = await fetch(attachment.signed_url);
    if (!response.ok) {
      return Result.err(`Failed to download: ${response.statusText}`);
    }

    const blob = await response.blob();

    // Create object URL and trigger download
    const objectUrl = URL.createObjectURL(blob);
    triggerDownload(objectUrl, filename);
    URL.revokeObjectURL(objectUrl);

    return Result.ok(undefined);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};
